// The strangler seam. Every extracted engine module replaces its pristine
// window global through here, so the migration's moving parts stay visible in
// one registry — and are deletable wholesale at step 14 cutover.

/** The pristine game's globals are untyped; this is the one sanctioned hole. */
export const legacyWindow = window as unknown as Record<string, unknown>

interface ShimRecord {
  name: string
  original: unknown
}

const installed: ShimRecord[] = []

/**
 * Replace a pristine window global with an extracted implementation.
 * The original is retained for reference and for `restoreGlobal` (test use).
 * Throws if the global doesn't exist — a shim for a name the pristine code
 * never defined is a typo, not a migration.
 */
export function replaceGlobal(name: string, impl: unknown): void {
  if (!(name in legacyWindow)) {
    throw new Error(`replaceGlobal: pristine global "${name}" does not exist`)
  }
  installed.push({ name, original: legacyWindow[name] })
  legacyWindow[name] = impl
}

/** Undo one shim (last-in wins if the same name was shimmed twice). */
export function restoreGlobal(name: string): void {
  for (let i = installed.length - 1; i >= 0; i--) {
    const rec = installed[i]
    if (rec && rec.name === name) {
      legacyWindow[name] = rec.original
      installed.splice(i, 1)
      return
    }
  }
  throw new Error(`restoreGlobal: no shim installed for "${name}"`)
}

/** Names currently shimmed, in installation order. */
export function installedShims(): string[] {
  return installed.map(r => r.name)
}

/** A promoted global's storage: module-owned state behind a window accessor. */
export interface Box<T> {
  value: T
}

const UNSET: unique symbol = Symbol('unset')
const bridges: string[] = []

/**
 * Promote a bare window global to module-owned state: bare-identifier reads
 * and writes in the pristine scripts resolve through this accessor pair from
 * now on. Seeds the box from whatever the window currently holds (top-level
 * init value — or, for platinumUpgrade6Effect, the named-access DOM element
 * whose Decimal.mul()===0 one-tick quirk must survive promotion).
 *
 * A global the pristine code has not defined yet stays "unset": reading it
 * throws the same ReferenceError the browser would have thrown, so promotion
 * never papers over a genuine read-before-write.
 */
export function installWindowBridge<T>(name: string, box: Box<T>): void {
  let backing: T | typeof UNSET = name in legacyWindow ? (legacyWindow[name] as T) : UNSET
  if (backing !== UNSET) box.value = backing

  Object.defineProperty(legacyWindow, name, {
    configurable: true,
    enumerable: true,
    get() {
      if (backing === UNSET) throw new ReferenceError(`${name} is not defined`)
      return box.value
    },
    set(v: T) {
      backing = v
      box.value = v
    },
  })
  bridges.push(name)
}

/** Names currently bridged, in installation order. */
export function installedBridges(): string[] {
  return [...bridges]
}

const exposed: string[] = []

/**
 * Publish a NEW global for redirected pristine call sites (canonical
 * formulas, etc.). The inverse precondition of replaceGlobal: the name must
 * NOT already exist — colliding with a pristine global is a bug.
 */
export function exposeGlobal(name: string, impl: unknown): void {
  if (name in legacyWindow) throw new Error(`exposeGlobal: "${name}" already exists on window`)
  legacyWindow[name] = impl
  exposed.push(name)
}

/** Names published via exposeGlobal, in order. */
export function exposedGlobals(): string[] {
  return [...exposed]
}
